/**
 * Barcode Generator Editor
 * Handles configuration for barcode generation controls
 * - Various barcode formats
 * - Size and styling options
 */

import React from 'react';
import type { PropertyEditor, PropertyEditorProps } from '../../../registry/PropertyEditor.types';

interface BarcodeGeneratorConfig {
  value?: string;
  format?: 'CODE128' | 'CODE39' | 'EAN13' | 'EAN8' | 'UPC' | 'ITF';
  width?: number;
  height?: number;
  displayValue?: boolean;
  fontSize?: number;
  margin?: number;
  lineColor?: string;
  backgroundColor?: string;
  enableDownload?: boolean;
  downloadFormat?: 'png' | 'jpeg' | 'svg';
}

/**
 * Render function for barcode generator editor
 */
function renderBarcodeGeneratorEditor(props: PropertyEditorProps) {
  const { control, onConfigChange } = props;
  const config = (control.config as BarcodeGeneratorConfig) || {};

  const value = config.value || '';
  const format = config.format ?? 'CODE128';
  const width = config.width ?? 2;
  const height = config.height ?? 100;
  const displayValue = config.displayValue ?? true;
  const fontSize = config.fontSize ?? 14;
  const margin = config.margin ?? 10;
  const lineColor = config.lineColor ?? '#000000';
  const backgroundColor = config.backgroundColor ?? '#FFFFFF';
  const downloadFormat = config.downloadFormat ?? 'png';

  return (
    <div className="af-properties-tab__section">
      <h4 className="af-properties-tab__subsection-title">Barcode Generator Configuration</h4>

      <div className="af-properties-tab__group">
        <label className="af-properties-tab__label">
          Value to Encode
          <input
            type="text"
            value={value}
            onChange={(e) => onConfigChange('value', e.target.value)}
            className="af-properties-tab__input"
            placeholder="e.g., 123456789"
          />
        </label>
      </div>

      <div className="af-properties-tab__group">
        <label className="af-properties-tab__label">
          Barcode Format
          <select
            value={format}
            onChange={(e) => onConfigChange('format', e.target.value)}
            className="af-properties-tab__select"
          >
            <option value="CODE128">CODE128 (alphanumeric)</option>
            <option value="CODE39">CODE39 (alphanumeric)</option>
            <option value="EAN13">EAN-13 (13 digits)</option>
            <option value="EAN8">EAN-8 (8 digits)</option>
            <option value="UPC">UPC-A (12 digits)</option>
            <option value="ITF">ITF-14 (14 digits)</option>
          </select>
        </label>
      </div>

      <div className="af-properties-tab__group">
        <label className="af-properties-tab__label">
          Bar Width (pixels)
          <input
            type="number"
            value={width}
            onChange={(e) => onConfigChange('width', Math.max(1, parseInt(e.target.value, 10) || 2))}
            className="af-properties-tab__input"
            min="1"
          />
        </label>
      </div>

      <div className="af-properties-tab__group">
        <label className="af-properties-tab__label">
          Bar Height (pixels)
          <input
            type="number"
            value={height}
            onChange={(e) => onConfigChange('height', Math.max(20, parseInt(e.target.value, 10) || 100))}
            className="af-properties-tab__input"
            min="20"
          />
        </label>
      </div>

      <div className="af-properties-tab__group">
        <label className="af-properties-tab__checkbox-label">
          <input
            type="checkbox"
            checked={displayValue}
            onChange={(e) => onConfigChange('displayValue', e.target.checked)}
          />
          Display Value Below Barcode
        </label>
      </div>

      {displayValue && (
        <div className="af-properties-tab__group">
          <label className="af-properties-tab__label">
            Font Size (pixels)
            <input
              type="number"
              value={fontSize}
              onChange={(e) => onConfigChange('fontSize', Math.max(8, parseInt(e.target.value, 10) || 14))}
              className="af-properties-tab__input"
              min="8"
            />
          </label>
        </div>
      )}

      <div className="af-properties-tab__group">
        <label className="af-properties-tab__label">
          Margin (pixels)
          <input
            type="number"
            value={margin}
            onChange={(e) => onConfigChange('margin', Math.max(0, parseInt(e.target.value, 10) || 10))}
            className="af-properties-tab__input"
            min="0"
          />
        </label>
      </div>

      <div className="af-properties-tab__group">
        <label className="af-properties-tab__label">
          Line Color
          <input
            type="color"
            value={lineColor}
            onChange={(e) => onConfigChange('lineColor', e.target.value)}
            className="af-properties-tab__color-input"
          />
        </label>
      </div>

      <div className="af-properties-tab__group">
        <label className="af-properties-tab__label">
          Background Color
          <input
            type="color"
            value={backgroundColor}
            onChange={(e) => onConfigChange('backgroundColor', e.target.value)}
            className="af-properties-tab__color-input"
          />
        </label>
      </div>

      <div className="af-properties-tab__group">
        <label className="af-properties-tab__checkbox-label">
          <input
            type="checkbox"
            checked={config.enableDownload !== false}
            onChange={(e) => onConfigChange('enableDownload', e.target.checked)}
          />
          Enable Download Button
        </label>
      </div>

      {config.enableDownload && (
        <div className="af-properties-tab__group">
          <label className="af-properties-tab__label">
            Download Format
            <select
              value={downloadFormat}
              onChange={(e) => onConfigChange('downloadFormat', e.target.value)}
              className="af-properties-tab__select"
            >
              <option value="png">PNG</option>
              <option value="jpeg">JPEG</option>
              <option value="svg">SVG</option>
            </select>
          </label>
        </div>
      )}

      <div className="af-properties-tab__info-box" style={{ marginTop: '20px', padding: '12px', backgroundColor: '#f0f7ff', borderRadius: '4px', fontSize: '12px', color: '#084298', border: '1px solid #b6d4fe' }}>
        <strong>Format Notes:</strong>
        <ul style={{ margin: '6px 0 0 20px', paddingLeft: '0' }}>
          <li>CODE128/39: Any alphanumeric content</li>
          <li>EAN-13: Exactly 13 digits</li>
          <li>EAN-8: Exactly 8 digits</li>
          <li>UPC-A: Exactly 12 digits</li>
        </ul>
      </div>
    </div>
  );
}

/**
 * BarcodeGeneratorEditor - Handles barcode generator control configuration
 */
export const BarcodeGeneratorEditor: PropertyEditor = {
  type: 'barcode-generator',
  label: 'Barcode Generator',
  description: 'Configure barcode generation with multiple formats and styles',
  render: renderBarcodeGeneratorEditor,
};
